import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import { theme } from '@prose-ui';

import { MetaData } from 'Components/MetaData';

import { productsCategories, productsSubCategories } from 'constants/products';

import { Faq } from 'Blocks/Faq';
import { ProductsCarousel } from 'Blocks/ProductsCarousel';
import { Ticker } from 'Blocks/Ticker';
import AccessoriesProductPageHero from '../Blocks/AccessoriesProductPageHero';

import ScrollTracker from 'Containers/ScrollTracker';

import { srcOrNextImageProp } from 'PropTypes/imageProps';
import productProps from 'PropTypes/productProps';

import { LegacyBaseScene } from 'Scenes/LegacyBaseScene';

import {
  getAccessoriesProductPageContent,
  getFilteredFetchedHaircareProductsIsLoaded,
} from 'dux/products/selectors';
import { useTrackProductViewed } from 'dux/products/hooks';

const AccessoriesProductDetails = ({ content, filteredFetchedProductsIsLoaded }) => {
  const { faq, heroModule, modules, productsModule, ticker } = content;

  useTrackProductViewed({
    category: productsCategories.OTHERS,
    subcategory: productsSubCategories.TOOLS,
    product: heroModule.product,
  });

  return (
    <>
      <MetaData
        description="Reduce frizz and preserve your hair with the Microfiber Hair Towel from Prose. Made from 100% recycled plastic bottles. Designed for all textures and lengths."
        title="Microfiber Hair Towel — Accessories | Prose"
      />

      <LegacyBaseScene footer FooterProps={{ hideCTA: true }} header justifyContent="start">
        <ScrollTracker>
          <AccessoriesProductPageHero content={heroModule} />
          <Ticker color={theme.colors.primary[400]} content={ticker} hasBorder showDash={false} />
          {modules.map(({ Component, key, children, props }) => (
            <Component key={key} {...props}>
              {children}
            </Component>
          ))}
          {filteredFetchedProductsIsLoaded && (
            <ProductsCarousel background={theme.colors.neutral[200]} content={productsModule} />
          )}
          <Faq
            backgroundColor={theme.colors.primary[400]}
            markupName="h3"
            questions={faq}
            shouldGenerateJsonld
            title="Frequently asked questions"
            variant="dark"
          />
        </ScrollTracker>
      </LegacyBaseScene>
    </>
  );
};

AccessoriesProductDetails.propTypes = {
  filteredFetchedProductsIsLoaded: PropTypes.bool.isRequired,
  content: PropTypes.shape({
    faq: PropTypes.arrayOf(
      PropTypes.shape({
        question: PropTypes.string,
        answer: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      })
    ),
    sustainability: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      logos: PropTypes.objectOf(srcOrNextImageProp),
    }),
    productsModule: PropTypes.shape({ headline: PropTypes.string }),
    ticker: PropTypes.arrayOf(PropTypes.string).isRequired,
    modules: PropTypes.arrayOf(
      PropTypes.shape({
        wideScreenOrder: PropTypes.string,
        images: PropTypes.shape({ mobile: srcOrNextImageProp, desktop: srcOrNextImageProp }),
        content: PropTypes.element,
      })
    ),
    heroModule: PropTypes.shape({
      imageCarousel: PropTypes.arrayOf(
        PropTypes.shape({
          image: srcOrNextImageProp,
          alt: PropTypes.string,
        })
      ),
      product: productProps.isRequired,
    }),
  }).isRequired,
};

export default connect((state, { productSlug }) => ({
  content: getAccessoriesProductPageContent(state, { productSlug }),
  filteredFetchedProductsIsLoaded: getFilteredFetchedHaircareProductsIsLoaded(state),
}))(AccessoriesProductDetails);
